import {useEffect, useLayoutEffect, useRef, useState} from 'react';

const ROTATION_INTERVAL = 6_000;

function resolvePosition(position, entries) {
    if (!entries.length) return {key: null, index: 0, page: 0};
    const matchingIndex = entries.findIndex(entry => entry.key === position.key);
    const index = matchingIndex >= 0 ? matchingIndex : Math.min(position.index, entries.length - 1);
    const page = matchingIndex >= 0 ? Math.min(position.page, entries[index].pages.length - 1) : 0;
    return {key: entries[index].key, index, page};
}

export default function useAlertRotation(entries, priority) {
    const [position, setPosition] = useState({key: null, index: 0, page: 0});
    const entriesRef = useRef(entries);
    const priorityRef = useRef(priority);
    const intervalRef = useRef(null);

    useLayoutEffect(() => {
        const escalated = priority > priorityRef.current;
        const previousEntries = entriesRef.current;
        entriesRef.current = entries;
        priorityRef.current = priority;
        setPosition(current => {
            let target = current;
            if (escalated) {
                target = {key: null, index: 0, page: 0};
            } else if (current.key && !entries.some(entry => entry.key === current.key)) {
                // If the current reason disappears, continue with its next surviving neighbour.
                const successors = [...previousEntries.slice(current.index + 1), ...previousEntries.slice(0, current.index)];
                const successor = successors.find(previous => entries.some(entry => entry.key === previous.key));
                target = {...current, key: successor?.key, page: 0};
            }
            const next = resolvePosition(target, entries);
            return next.key === current.key && next.index === current.index && next.page === current.page ? current : next;
        });
        // Only a higher severity starts a new reading interval; ordinary API updates do not.
        if (escalated && intervalRef.current !== null) {
            clearInterval(intervalRef.current);
            intervalRef.current = setInterval(() => setPosition(current => advance(current, entriesRef.current)), ROTATION_INTERVAL);
        }
    }, [entries, priority]);

    useEffect(() => {
        intervalRef.current = setInterval(() => setPosition(current => advance(current, entriesRef.current)), ROTATION_INTERVAL);
        return () => clearInterval(intervalRef.current);
    }, []);

    return resolvePosition(position, entries);
}

function advance(position, entries) {
    if (!entries.length) return position;
    const current = resolvePosition(position, entries);
    if (current.page + 1 < entries[current.index].pages.length) return {...current, page: current.page + 1};
    const index = (current.index + 1) % entries.length;
    if (index === current.index && current.page === 0) return position;
    return {key: entries[index].key, index, page: 0};
}
